import {
  createNativeStackNavigator,
  NativeStackHeaderProps,
} from '@react-navigation/native-stack';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Platform, View } from 'react-native';

import {
  AboutScreen,
  HelpScreen,
  ContactUsScreen,
  InviteFriendScreen,
  LanguageScreen,
  NotificationsScreen,
  ProfileScreen,
  SecurityScreen,
  SettingsScreen,
  SubscriptionScreen,
  UpdatePasswordScreen,
  UpdateProfileGeneralScreen,
  UpdateProfilePrivateScreen,
  CreditCardsScreen,
  UpdateCreditCardScreen,
  ChangeSubscriptionPlanScreen,
  PaymentScreen,
} from '@screens';
import { WebHeader, Header, BaseMobileHeader } from '@components';
import { screens } from './screens';
import type { ProfileStackParamListType } from './types';
import { useViewerModel } from '@models/Viewer/ViewerModel';

function screenHeader(props: NativeStackHeaderProps) {
  return Platform.select({
    web: <WebHeader activeTabButtonName={screens.Profile} />,
    default: <Header {...props} />,
  });
}

const ProfileStack =
  createNativeStackNavigator<ProfileStackParamListType>();

export const ProfileNavigator = observer(() => {
  const viewer = useViewerModel();

  // Prevent access to the rest of the app when subscription plan is completely inactive.
  // Also, check for current user to be defined to get proper tariff plan status,
  // otherwise Subscription screen will be always rendered initially.
  if (viewer.currentUser && !viewer.currentUser.tariffPlan.isActive) {
    return (
      <ProfileStack.Navigator
        initialRouteName={screens.Subscription}
        screenOptions={{
          headerShown: true,
          header: screenHeader,
        }}
      >
        <ProfileStack.Screen
          component={SubscriptionScreen}
          name={screens.Subscription}
          options={{
            header: (props) =>
              Platform.select({
                web: (
                  <WebHeader activeTabButtonName={screens.Profile} />
                ),
                default: (
                  <BaseMobileHeader
                    title={props.options.title}
                    LeftComponent={() => <View />}
                  />
                ),
              }),
          }}
        />
        <ProfileStack.Screen
          component={CreditCardsScreen}
          name={screens.CreditCards}
        />
        <ProfileStack.Screen
          component={UpdateCreditCardScreen}
          name={screens.UpdateCreditCard}
        />
        <ProfileStack.Screen
          component={ChangeSubscriptionPlanScreen}
          name={screens.ChangeSubscriptionPlan}
        />
        <ProfileStack.Screen
          component={PaymentScreen}
          name={screens.Payment}
        />
      </ProfileStack.Navigator>
    );
  }

  return (
    <ProfileStack.Navigator
      initialRouteName={screens.Profile}
      screenOptions={{
        headerShown: true,
        header: screenHeader,
      }}
    >
      <ProfileStack.Screen
        component={ProfileScreen}
        name={screens.Profile}
      />
      <ProfileStack.Screen
        component={UpdateProfileGeneralScreen}
        name={screens.UpdateProfileGeneral}
      />
      <ProfileStack.Screen
        component={UpdateProfilePrivateScreen}
        name={screens.UpdateProfilePrivate}
      />
      <ProfileStack.Screen
        component={SettingsScreen}
        name={screens.Settings}
      />
      <ProfileStack.Screen
        component={NotificationsScreen}
        name={screens.Notifications}
      />
      <ProfileStack.Screen
        component={SubscriptionScreen}
        name={screens.Subscription}
      />
      <ProfileStack.Screen
        component={LanguageScreen}
        name={screens.Language}
      />
      <ProfileStack.Screen
        component={SecurityScreen}
        name={screens.Security}
      />
      <ProfileStack.Screen
        component={UpdatePasswordScreen}
        name={screens.UpdatePassword}
      />
      <ProfileStack.Screen
        component={InviteFriendScreen}
        name={screens.InviteFriend}
      />
      <ProfileStack.Screen
        component={HelpScreen}
        name={screens.Help}
      />
      <ProfileStack.Screen
        component={ContactUsScreen}
        name={screens.ContactUs}
      />
      <ProfileStack.Screen
        component={AboutScreen}
        name={screens.About}
      />
      <ProfileStack.Screen
        component={CreditCardsScreen}
        name={screens.CreditCards}
      />
      <ProfileStack.Screen
        component={UpdateCreditCardScreen}
        name={screens.UpdateCreditCard}
      />
      <ProfileStack.Screen
        component={ChangeSubscriptionPlanScreen}
        name={screens.ChangeSubscriptionPlan}
      />
      <ProfileStack.Screen
        component={PaymentScreen}
        name={screens.Payment}
      />
    </ProfileStack.Navigator>
  );
});
